import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * Live-from-Settings rendering for approval-chain stage labels.
 *
 * Why this exists: the sealed `ApprovalChainEntry.ruleName` is a snapshot
 * taken when the PO / Requisition was created. If the user later edits an
 * ApprovalRule's `minAmount`, `maxAmount`, or `name` in Settings, the sealed
 * string still shows the old bracket — so the dashboard timeline disagrees
 * with the configured rules.
 *
 * Solution: load every active ApprovalRule once per session, key by id, and
 * derive a "name · ₹min–₹max" label from the LIVE rule whenever we render a
 * chain stage. Falls back to the sealed name if the rule was deleted.
 */
export interface RuleLite {
  _id: string;
  name: string;
  minAmount: number;
  maxAmount: number | null;
}

export function formatRuleBracket(rule: { minAmount?: number | null; maxAmount?: number | null }): string {
  const fmt = (n: number) => '₹' + Number(n).toLocaleString('en-IN');
  const min = Number(rule.minAmount) || 0;
  if (rule.maxAmount == null) return `${fmt(min)}+`;
  return `${fmt(min)}–${fmt(Number(rule.maxAmount) || 0)}`;
}

/**
 * Build the display label for one chain stage. Prefers the live rule's name
 * + bracket; falls back to the sealed ruleName when the rule no longer
 * exists (deleted by an admin since the PO was raised).
 */
export function describeStage(
  stage: { ruleId?: string | null; ruleName?: string },
  rulesById: Map<string, RuleLite> | null,
): { name: string; bracket: string | null; full: string } {
  const rule = stage.ruleId && rulesById ? rulesById.get(String(stage.ruleId)) : undefined;
  if (rule) {
    const bracket = formatRuleBracket(rule);
    return { name: rule.name, bracket, full: `${rule.name} · ${bracket}` };
  }
  const name = stage.ruleName || '—';
  return { name, bracket: null, full: name };
}

/**
 * Fetch the active ApprovalRules and return a quick lookup by id. Cached for
 * a minute so repeated chain renders don't re-fetch.
 */
export function useApprovalRulesMap(): Map<string, RuleLite> | null {
  const { data } = useQuery<RuleLite[]>({
    queryKey: ['approval-rules-for-chain-labels'],
    queryFn: () => api.get('/api/approval-rules'),
    staleTime: 60_000,
  });
  if (!data) return null;
  return new Map(data.map((r) => [String(r._id), r]));
}
